import React from "react";

/**
 * Custom themed SVG illustrations for the Home page feature cards.
 * Line-art style matching the StrainScan Pro HUD aesthetic — neon green stroke,
 * leaf motifs, infinite-sharp vector. Pass `size` to scale.
 */

const stroke = "var(--ss-green)";

function Frame({ size = 88, children }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      stroke={stroke}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ filter: "drop-shadow(0 0 8px rgba(157,252,4,0.35))" }}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

// Tiny leaf used as a recurring motif inside several illustrations
function MiniLeaf({ x = 50, y = 50, scale = 1, fill = "rgba(157,252,4,0.18)" }) {
  const angles = [0, 30, -30, 60, -60, 90, -90];
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} fill={fill} stroke={stroke} strokeWidth="1">
      {angles.map((a) => (
        <ellipse
          key={a}
          cx="0"
          cy="-9"
          rx="2.4"
          ry="9"
          transform={`rotate(${a})`}
        />
      ))}
      <line x1="0" y1="0" x2="0" y2="6" />
    </g>
  );
}

/* 1. Product Recognition — leaf in scan-frame with corner brackets + scan line */
export function StrainRecognitionIcon({ size }) {
  return (
    <Frame size={size}>
      {/* corner brackets */}
      <path d="M14 30 V14 H30" />
      <path d="M70 14 H86 V30" />
      <path d="M86 70 V86 H70" />
      <path d="M30 86 H14 V70" />
      {/* leaf inside */}
      <g transform="translate(50 56)">
        {[0, 30, -30, 60, -60, 88, -88].map((a) => (
          <ellipse
            key={a}
            cx="0"
            cy="-14"
            rx="3.2"
            ry="14"
            transform={`rotate(${a})`}
            fill="rgba(157,252,4,0.18)"
          />
        ))}
        <line x1="0" y1="0" x2="0" y2="9" />
      </g>
      {/* scan line */}
      <line x1="18" y1="50" x2="82" y2="50" stroke="var(--ss-green)" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.85" />
      <circle cx="50" cy="50" r="2" fill={stroke} />
    </Frame>
  );
}

/* 2. Potency Insights — Erlenmeyer flask with leaf inside + magnifying glass */
export function PotencyInsightsIcon({ size }) {
  return (
    <Frame size={size}>
      {/* flask */}
      <path d="M40 18 H60 V32 L74 64 Q78 78 64 80 H36 Q22 78 26 64 L40 32 Z" />
      <line x1="40" y1="22" x2="60" y2="22" />
      {/* liquid */}
      <path d="M30 60 Q50 56 70 60 L72 70 Q50 74 28 70 Z" fill="rgba(157,252,4,0.22)" stroke="none" />
      {/* mini leaf inside flask */}
      <MiniLeaf x="50" y="62" scale="0.55" />
      {/* bubbles */}
      <circle cx="44" cy="50" r="1.2" fill={stroke} />
      <circle cx="56" cy="44" r="0.9" fill={stroke} />
      {/* magnifying glass over neck */}
      <circle cx="68" cy="22" r="9" />
      <line x1="74" y1="28" x2="82" y2="36" />
    </Frame>
  );
}

/* 3. Quality Assessment — shield with leaf and checkmark */
export function QualityAssessmentIcon({ size }) {
  return (
    <Frame size={size}>
      <path d="M50 12 L80 22 V50 Q80 72 50 86 Q20 72 20 50 V22 Z" fill="rgba(157,252,4,0.08)" />
      {/* leaf inside top half */}
      <g transform="translate(50 44)">
        {[0, 32, -32, 65, -65].map((a) => (
          <ellipse key={a} cx="0" cy="-10" rx="2.4" ry="10" transform={`rotate(${a})`} fill="rgba(157,252,4,0.22)" />
        ))}
      </g>
      {/* large check */}
      <path d="M36 60 L46 70 L66 50" strokeWidth="2.2" />
    </Frame>
  );
}

/* 4. Batch Tracking — bars with arrow up + leaf accent */
export function BatchTrackingIcon({ size }) {
  return (
    <Frame size={size}>
      {/* axis */}
      <path d="M16 80 H84" />
      <path d="M16 80 V20" />
      {/* bars */}
      <rect x="24" y="60" width="10" height="20" fill="rgba(157,252,4,0.25)" />
      <rect x="40" y="48" width="10" height="32" fill="rgba(157,252,4,0.35)" />
      <rect x="56" y="38" width="10" height="42" fill="rgba(157,252,4,0.5)" />
      <rect x="72" y="26" width="10" height="54" fill="rgba(157,252,4,0.7)" />
      {/* trend arrow */}
      <path d="M22 56 L40 42 L56 32 L78 18" strokeWidth="1.6" />
      <path d="M70 18 H78 V26" strokeWidth="1.6" />
      {/* leaf */}
      <MiniLeaf x="22" y="32" scale="0.5" fill="rgba(157,252,4,0.4)" />
    </Frame>
  );
}

/* 5. Education & Guidance — open book with leaf */
export function EducationIcon({ size }) {
  return (
    <Frame size={size}>
      {/* book covers */}
      <path d="M14 26 Q32 22 48 30 V78 Q32 70 14 74 Z" fill="rgba(157,252,4,0.06)" />
      <path d="M86 26 Q68 22 52 30 V78 Q68 70 86 74 Z" fill="rgba(157,252,4,0.06)" />
      {/* spine */}
      <line x1="50" y1="30" x2="50" y2="78" />
      {/* page lines */}
      <line x1="20" y1="38" x2="42" y2="36" />
      <line x1="20" y1="46" x2="42" y2="44" />
      <line x1="20" y1="54" x2="42" y2="52" />
      <line x1="58" y1="36" x2="80" y2="38" />
      <line x1="58" y1="44" x2="80" y2="46" />
      <line x1="58" y1="52" x2="80" y2="54" />
      {/* leaf overlay */}
      <MiniLeaf x="50" y="62" scale="0.7" fill="rgba(157,252,4,0.3)" />
    </Frame>
  );
}

/* 6. Compliance Ready — clipboard with checkmark and leaf */
export function ComplianceIcon({ size }) {
  return (
    <Frame size={size}>
      {/* Clip at top */}
      <rect x="42" y="10" width="16" height="10" rx="2" fill="rgba(157,252,4,0.3)" />
      <line x1="46" y1="14" x2="54" y2="14" strokeWidth="2" />
      {/* Clipboard body */}
      <rect x="22" y="18" width="56" height="72" rx="3" fill="rgba(157,252,4,0.06)" />
      {/* Document lines */}
      <line x1="32" y1="36" x2="68" y2="36" />
      <line x1="32" y1="44" x2="60" y2="44" />
      <line x1="32" y1="52" x2="64" y2="52" />
      {/* Mini leaf badge */}
      <MiniLeaf x="34" y="70" scale="0.5" fill="rgba(157,252,4,0.4)" />
      {/* Big check */}
      <path d="M44 74 L52 82 L70 64" strokeWidth="2.4" />
    </Frame>
  );
}
